import {
  Box,
  Button,
  Pagination,
  Skeleton,
  Table,
  Text,
  createStyles,
} from "@mantine/core";
import { IconArrowLeft, IconExternalLink } from "@tabler/icons-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { enterProviderView } from "../../../services/skypointProvider";

const useStyles = createStyles((theme) => ({
  container: { width: "100%" },
  stickyBanner: { position: "sticky", top: 0, zIndex: 100 },
  content: {
    padding: "24px 28px",
    [theme.fn.smallerThan("sm")]: { padding: "16px 12px" },
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    color: "#667085",
    cursor: "pointer",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    marginBottom: 12,
    "&:hover": { color: "#101828" },
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    marginBottom: 20,
  },
  headerIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  title: {
    fontFamily: "Inter",
    fontSize: 22,
    fontWeight: 700,
    color: "#101828",
    lineHeight: 1.2,
  },
  subtitle: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "#667085",
    marginTop: 2,
  },
  tableWrap: {
    background: "#FFFFFF",
    border: "1px solid #EAECF0",
    borderRadius: 12,
    overflow: "hidden",
  },
  table: {
    "& thead tr th": {
      backgroundColor: "#F9FAFB",
      fontFamily: "Inter",
      fontSize: 12,
      fontWeight: 600,
      color: "#667085",
      textTransform: "uppercase",
      letterSpacing: 0.4,
      borderBottom: "1px solid #EAECF0",
    },
    "& tbody tr td": {
      fontFamily: "Inter",
      fontSize: 13,
      color: "#101828",
      borderBottom: "1px solid #F2F4F7",
      verticalAlign: "middle",
    },
    "& tbody tr:last-of-type td": { borderBottom: "none" },
  },
  emptyState: {
    padding: "48px 20px",
    textAlign: "center",
    color: "#667085",
    fontFamily: "Inter",
    fontSize: 14,
  },
  paginationRow: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
    borderTop: "1px solid #EAECF0",
  },
}));

/**
 * Shared table page for Skypoint command-center tile detail views.
 *
 * Each tile detail page is just a thin wrapper that supplies:
 *   - title, description, Icon
 *   - iconColor / iconBg
 *   - columns: [{ key, header, width?, render: (item) => ReactNode }]
 *   - stateSelector: Redux selector returning { items, total, loading }
 *   - fetcher: ({ page, limit }) => Promise<void> that dispatches into state
 *   - emptyMessage
 *   - showOgisoCoreLink (default true) — render the Ogiso Core button column
 *
 * Pagination, header, back link, Ogiso Core navigation, and empty/loading
 * states are handled here so per-tile code stays small.
 */
const SkypointAlertsTablePage = ({
  isSidebarOpen,
  title,
  description,
  Icon,
  iconColor = "#F04438",
  iconBg = "#FEF3F2",
  columns,
  stateSelector,
  fetcher,
  emptyMessage = "No items in this category.",
  showOgisoCoreLink = true,
  itemUnit = "item",
}) => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const data = useSelector(stateSelector) || {};
  const items = data.items || [];
  const total = data.total || 0;
  const loading = !!data.loading;

  const [page, setPage] = useState(1);
  const LIMIT = 25;

  useEffect(() => {
    fetcher({ page, limit: LIMIT });
  }, [page]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total]
  );

  const handleViewProvider = async (providerId, providerName) => {
    const ok = await enterProviderView(providerId, providerName);
    if (ok) navigate("/home/provider-view");
  };

  const totalCols = columns.length + (showOgisoCoreLink ? 1 : 0);

  return (
    <Box className={classes.container}>
      <Box className={classes.stickyBanner}>
        <AppNavbar
          title="Home"
          profile={profile}
          isSidebarOpen={isSidebarOpen}
        />
      </Box>

      <Box className={classes.content}>
        <Box
          className={classes.backRow}
          onClick={() => navigate("/home/dashboard-skypoint")}
        >
          <IconArrowLeft size={16} />
          <span>Back to dashboard</span>
        </Box>

        <Box className={classes.headerRow}>
          <Box className={classes.headerIcon} sx={{ backgroundColor: iconBg }}>
            <Icon size={22} color={iconColor} />
          </Box>
          <Box>
            <Text className={classes.title}>{title}</Text>
            <Text className={classes.subtitle}>
              {description}
              {total > 0 &&
                ` · ${total} ${total === 1 ? itemUnit : `${itemUnit}s`}`}
            </Text>
          </Box>
        </Box>

        <Box className={classes.tableWrap}>
          <Table className={classes.table} verticalSpacing="md">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} style={c.width ? { width: c.width } : null}>
                    {c.header}
                  </th>
                ))}
                {showOgisoCoreLink && <th style={{ width: 160 }}></th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={totalCols}>
                      <Skeleton height={32} radius={6} />
                    </td>
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={totalCols} className={classes.emptyState}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.relatedId || item._id || Math.random()}>
                    {columns.map((c) => (
                      <td key={c.key}>{c.render(item)}</td>
                    ))}
                    {showOgisoCoreLink && (
                      <td>
                        <Button
                          size="xs"
                          variant="subtle"
                          leftIcon={<IconExternalLink size={14} />}
                          onClick={() =>
                            handleViewProvider(
                              item.providerId,
                              item.providerName
                            )
                          }
                        >
                          Ogiso Core
                        </Button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          {totalPages > 1 && (
            <Box className={classes.paginationRow}>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
                size="sm"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SkypointAlertsTablePage;
